import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Sparkles, Save, X, Send, BookOpen } from "lucide-react";

/**
 * Reusable list of AI prompts with inline create/edit/delete.
 *
 * Props:
 *  - listUrl       GET → returns array of prompts
 *  - createUrl     POST { label, prompt_text, also_master? } → creates
 *  - updateBase    PATCH `${updateBase}/${promptId}` → updates
 *  - deleteBase    DELETE `${deleteBase}/${promptId}`
 *  - showAlsoMaster (bool) — only true on client-scope lists
 *  - onUsePrompt   (text) => void — when provided, each prompt becomes a clickable chip
 *  - title / subtitle
 */
export default function AIPromptList({
  listUrl,
  createUrl,
  updateBase = "/ai-prompts",
  deleteBase = "/ai-prompts",
  showAlsoMaster = false,
  onUsePrompt,
  title = "Prompt library",
  subtitle = "Edit, add or delete saved questions.",
}) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // prompt object
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftText, setDraftText] = useState("");
  const [alsoMaster, setAlsoMaster] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(listUrl);
      setItems(data || []);
    } catch (e) {
      setItems([]);
    }
  }, [listUrl]);

  useEffect(() => { load(); }, [load]);

  const startAdd = () => {
    setEditing(null);
    setAdding(true);
    setDraftLabel("");
    setDraftText("");
    setAlsoMaster(false);
  };

  const startEdit = (p) => {
    setAdding(false);
    setEditing(p);
    setDraftLabel(p.label);
    setDraftText(p.prompt_text);
    setAlsoMaster(false);
  };

  const cancel = () => {
    setAdding(false); setEditing(null);
    setDraftLabel(""); setDraftText(""); setAlsoMaster(false);
  };

  const save = async () => {
    if (!draftLabel.trim() || !draftText.trim()) return;
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`${updateBase}/${editing.prompt_id}`, {
          label: draftLabel.trim(),
          prompt_text: draftText.trim(),
        });
        toast.success("Prompt updated");
      } else {
        const body = {
          label: draftLabel.trim(),
          prompt_text: draftText.trim(),
        };
        if (showAlsoMaster) body.also_master = alsoMaster;
        await api.post(createUrl, body);
        toast.success(
          showAlsoMaster && alsoMaster
            ? "Prompt added (also pushed to Master)"
            : "Prompt added"
        );
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    try {
      await api.delete(`${deleteBase}/${p.prompt_id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-prompt-list">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline flex items-center gap-1">
            <BookOpen className="h-3 w-3" /> {title}
          </div>
          <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>
        </div>
        <Button onClick={startAdd}
          className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white h-9 text-xs"
          data-testid="prompt-add-button">
          <Plus className="h-3.5 w-3.5 mr-1" /> New prompt
        </Button>
      </div>

      {(adding || editing) && (
        <Card className="rounded-sm border-[#002FA7]/30 bg-[#F4F5FB]" data-testid="prompt-edit-card">
          <CardContent className="p-5 space-y-3">
            <div>
              <Label className="label-overline">Label</Label>
              <Input
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                placeholder="e.g. Identify low-CTR ad copy"
                className="rounded-sm h-10 bg-white mt-1"
                data-testid="prompt-label-input"
              />
            </div>
            <div>
              <Label className="label-overline">Prompt text</Label>
              <Textarea
                rows={4}
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder="The full question or instruction we'll send to the AI."
                className="rounded-sm bg-white mt-1"
                data-testid="prompt-text-input"
              />
            </div>
            {showAlsoMaster && !editing && (
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none"
                data-testid="also-master-label">
                <input
                  type="checkbox"
                  checked={alsoMaster}
                  onChange={(e) => setAlsoMaster(e.target.checked)}
                  className="h-4 w-4 accent-[#002FA7]"
                  data-testid="also-master-checkbox"
                />
                <span>
                  Also add this prompt to the <strong>Master AI Prompts</strong> library
                  <span className="text-zinc-500"> — every future client will inherit it.</span>
                </span>
              </label>
            )}
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
              <Button onClick={save} disabled={busy || !draftLabel.trim() || !draftText.trim()}
                className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="prompt-save-button">
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {busy ? "Saving…" : (editing ? "Save changes" : "Add prompt")}
              </Button>
              <Button variant="ghost" className="rounded-sm" onClick={cancel}
                data-testid="prompt-cancel-button">
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {items.map((p) => (
          <Card key={p.prompt_id} className="rounded-sm border-zinc-200 hover:border-[#002FA7]/40 transition-colors"
            data-testid={`prompt-row-${p.prompt_id}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="h-8 w-8 bg-zinc-100 text-[#002FA7] rounded-sm flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm">{p.label}</div>
                <p className="text-xs text-zinc-600 mt-0.5 leading-relaxed line-clamp-2">
                  {p.prompt_text}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {onUsePrompt && (
                  <Button size="sm" variant="ghost"
                    className="rounded-sm text-xs text-[#002FA7] hover:bg-[#002FA7]/10"
                    onClick={() => onUsePrompt(p.prompt_text)}
                    data-testid={`prompt-use-${p.prompt_id}`}>
                    <Send className="h-3 w-3 mr-1" /> Use
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="rounded-sm"
                  onClick={() => startEdit(p)}
                  data-testid={`prompt-edit-${p.prompt_id}`}>
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                      data-testid={`prompt-delete-${p.prompt_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-sm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete &ldquo;{p.label}&rdquo;?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This only removes it from this list. Existing chat history is unaffected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                        onClick={() => remove(p)}
                        data-testid={`prompt-confirm-delete-${p.prompt_id}`}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && !adding && (
          <Card className="rounded-sm border-dashed border-zinc-300">
            <CardContent className="p-8 text-center text-sm text-zinc-500">
              No prompts yet — click <strong>New prompt</strong> above to add one.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
